import { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  Shield,
  Eye,
  Users,
  FileText,
  Download,
  Search,
  AlertTriangle,
  Clock,
  Database,
  Activity,
  GitBranch,
  Hash
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { useTrustChainFeed } from '../hooks/TrustChainHook';
import { fetchAgents, fetchBlackboxAudit } from '../lib/api';
import { safeFallback } from '../lib/production-guard';
import { useDemoMode } from './DemoContext';
import { SAMPLE_DATA_BADGE, sampleDataDisclaimer } from '../lib/disclaimers.generated';

function useSafeDemoMode() {
  try {
    return useDemoMode();
  } catch {
    return { isDemoMode: false, activeScenario: null, scenarios: [], setIsDemoMode: () => {}, setActiveScenario: () => {} };
  }
}

interface ChainMetrics {
  totalRecords: number;
  overrideRate: number;
  averageApprovalTime: number;
  securityScore: number;
  lastAudit: string;
}

// Fallback data used when API is unreachable
const fallbackTrustChainData = [
  { timestamp: '2026-05-01T10:00:00Z', capsule: 'EnterpriseUIModule', approver: 'G3X', override: false, hash: '0x1a2b3c4d5e6f7890', action: 'DEPLOYMENT', severity: 'low', duration: 45 },
  { timestamp: '2026-05-01T10:15:00Z', capsule: 'SecurityModule', approver: 'G1X', override: true, hash: '0x3c4d5e6f78901a2b', action: 'EMERGENCY_OVERRIDE', severity: 'high', duration: 12 },
  { timestamp: '2026-05-01T10:30:00Z', capsule: 'SalesEnablementModule', approver: 'G2X', override: false, hash: '0x9b8c7d6e5f4a3210', action: 'CONFIGURATION_UPDATE', severity: 'medium', duration: 28 },
  { timestamp: '2026-05-01T10:45:00Z', capsule: 'TrustChainMapModule', approver: 'G4X', override: true, hash: '0x5f4e3d2c1b0a9876', action: 'PERSONA_ESCALATION', severity: 'critical', duration: 8 },
  { timestamp: '2026-05-01T11:00:00Z', capsule: 'AIIntegrationModule', approver: 'G3X', override: false, hash: '0x7890abcdef123456', action: 'ROUTINE_UPDATE', severity: 'low', duration: 67 }
];

const fallbackOverrideTimeline = [
  { time: '06:00', overrides: 2, approvals: 8, alerts: 0 },
  { time: '08:00', overrides: 1, approvals: 12, alerts: 1 },
  { time: '10:00', overrides: 4, approvals: 15, alerts: 2 },
  { time: '12:00', overrides: 3, approvals: 10, alerts: 0 },
  { time: '14:00', overrides: 2, approvals: 14, alerts: 1 },
  { time: '16:00', overrides: 1, approvals: 9, alerts: 0 },
  { time: '18:00', overrides: 0, approvals: 6, alerts: 0 }
];

const fallbackPersonaDistribution = [
  { persona: 'Analyst', count: 12, percentage: 35, color: '#00f5ff' },
  { persona: 'Builder', count: 8, percentage: 24, color: '#44ff44' },
  { persona: 'Operator', count: 10, percentage: 29, color: '#ffcc00' },
  { persona: 'Strategist', count: 4, percentage: 12, color: '#ff8844' }
];

export default function TrustChainMapUI() {
  const { isDemoMode: globalDemoMode, activeScenario } = useSafeDemoMode();
  // Hook manages its own polling/websocket lifecycle as a side effect; its return
  // value isn't consumed by this view (records are sourced from the API fetch below).
  useTrustChainFeed();
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');
  const [searchTerm, setSearchTerm] = useState('');
  const [chainMetrics, setChainMetrics] = useState<ChainMetrics>({
    totalRecords: 247,
    overrideRate: 12.5,
    averageApprovalTime: 34,
    securityScore: 94.7,
    lastAudit: '2026-05-01T08:30:00Z'
  });

  const [isLiveMode, setIsLiveMode] = useState(true);
  const [filteredRecords, setFilteredRecords] = useState(safeFallback(fallbackTrustChainData) || []);
  const [overrideTimelineData, setOverrideTimelineData] = useState(safeFallback(fallbackOverrideTimeline) || []);
  const [personaDistributionData, setPersonaDistributionData] = useState(safeFallback(fallbackPersonaDistribution) || []);
  const [isUsingDemoData, setIsUsingDemoData] = useState(true);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Apply demo scenario adjustments to integrity metrics
  useEffect(() => {
    if (globalDemoMode && activeScenario) {
      if (activeScenario.id === 'crisis-simulation') {
        setChainMetrics(prev => ({
          ...prev,
          securityScore: 67.2,
          overrideRate: 34.8,
          totalRecords: activeScenario.metrics.modules,
          averageApprovalTime: 8,
        }));
      } else if (activeScenario.id === 'enterprise-ops') {
        setChainMetrics(prev => ({
          ...prev,
          securityScore: 96.3,
          overrideRate: 8.2,
          totalRecords: activeScenario.metrics.modules * 3,
          averageApprovalTime: 22,
        }));
      } else if (activeScenario.id === 'growth-showcase') {
        setChainMetrics(prev => ({
          ...prev,
          securityScore: 91.0,
          overrideRate: 5.1,
          totalRecords: activeScenario.metrics.modules,
          averageApprovalTime: 45,
        }));
      }
    }
  }, [globalDemoMode, activeScenario]);

  // Fetch real trust chain data from API
  useEffect(() => {
    if (globalDemoMode) return; // Skip API polling during demo mode
    const loadTrustData = async () => {
      try {
        const [agentsData, auditData] = await Promise.all([
          fetchAgents(),
          fetchBlackboxAudit()
        ]);

        if (agentsData) {
          setIsUsingDemoData(false);
          const capsules = agentsData?.agents?.blackBox?.capsules || [];
          const adv = agentsData.agents?.advancedFeatures || {};

          // Build trust chain records from real capsule data
          const trustRecords = capsules.map((cap: any, idx: number) => ({
            timestamp: cap.lastModified || cap.createdAt || new Date().toISOString(),
            capsule: cap.name || cap.id || `Module-${String(idx + 1).padStart(3, '0')}`,
            approver: cap.approver || cap.persona || 'G3X',
            override: cap.override === true,
            hash: cap.hash || `0x${Math.random().toString(16).slice(2, 18)}`,
            action: cap.action || cap.status || 'ACTIVE',
            severity: cap.severity || cap.riskLevel || 'low',
            duration: cap.duration || Math.floor(Math.random() * 60) + 5
          }));

          if (trustRecords.length > 0) {
            setFilteredRecords(trustRecords);
          }

          // Build persona distribution from capsule persona assignments
          const personaCounts: Record<string, number> = {};
          capsules.forEach((cap: any) => {
            const p = cap.persona || cap.approver || 'G3X';
            personaCounts[p] = (personaCounts[p] || 0) + 1;
          });
          const total = Object.values(personaCounts).reduce((a: number, b: number) => a + b, 0) || 1;
          const personaColors: Record<string, string> = { 'G1X': '#00f5ff', 'G2X': '#44ff44', 'G3X': '#ffcc00', 'G4X': '#ff8844' };
          const personaLabels: Record<string, string> = { 'G1X': 'Analyst', 'G2X': 'Builder', 'G3X': 'Operator', 'G4X': 'Strategist' };
          const distData = Object.entries(personaCounts).map(([p, count]) => ({
            persona: personaLabels[p] || p,
            count: count as number,
            percentage: Math.round(((count as number) / total) * 100),
            color: personaColors[p] || '#666666'
          }));
          if (distData.length > 0) {
            setPersonaDistributionData(distData);
          }

          const overrideCount = trustRecords.filter((r: any) => r.override).length;
          setChainMetrics({
            totalRecords: capsules.length,
            overrideRate: capsules.length > 0 ? Math.round((overrideCount / capsules.length) * 1000) / 10 : 12.5,
            averageApprovalTime: adv.avgApprovalTime || 34,
            securityScore: adv.securityScore || adv.trustScore || 94.7,
            lastAudit: adv.lastAudit || auditData?.lastAudit || new Date().toISOString()
          });

          // Build override timeline from audit data if available
          if (auditData && Array.isArray(auditData.events)) {
            const buckets: Record<string, { overrides: number; approvals: number; alerts: number }> = {};
            auditData.events.forEach((evt: any) => {
              const hour = new Date(evt.timestamp).getHours();
              const key = `${String(hour).padStart(2, '0')}:00`;
              if (!buckets[key]) buckets[key] = { overrides: 0, approvals: 0, alerts: 0 };
              const bucket = buckets[key];
              if (!bucket) return;
              if (evt.type === 'override') bucket.overrides++;
              else if (evt.type === 'alert') bucket.alerts++;
              else bucket.approvals++;
            });
            const timeline = Object.entries(buckets)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([time, data]) => ({ time, ...data }));
            if (timeline.length > 0) {
              setOverrideTimelineData(timeline);
            }
          }
        }
      } catch (err) {
        console.error('TrustChainMapUI: API fetch failed, using fallback data', err);
      }
    };

    loadTrustData();
    pollRef.current = setInterval(loadTrustData, 15000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const getPersonaColor = (persona: string) => {
    switch(persona) {
      case 'G1X': return '#00f5ff';
      case 'G2X': return '#44ff44';
      case 'G3X': return '#ffcc00';
      case 'G4X': return '#ff8844';
      default: return '#666666';
    }
  };

  const getPersonaLevel = (persona: string) => {
    switch(persona) {
      case 'G1X': return 'Analyst';
      case 'G2X': return 'Builder';
      case 'G3X': return 'Operator';
      case 'G4X': return 'Strategist';
      default: return 'Unknown';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch(severity) {
      case 'critical': return 'text-ag-critical bg-red-900 border-red-600';
      case 'high': return 'text-orange-400 bg-orange-900 border-orange-600';
      case 'medium': return 'text-ag-warning bg-yellow-900 border-yellow-600';
      case 'low': return 'text-ag-active bg-green-900 border-green-600';
      default: return 'text-[#A8A29E] bg-gray-900 border-[#4A443D]';
    }
  };

  const exportTrustChainData = () => {
    const exportData = {
      timestamp: new Date().toISOString(),
      chainMetrics,
      records: filteredRecords,
      auditInfo: {
        exportedBy: 'Verification Visualizer',
        recordCount: filteredRecords.length,
        timeRange: selectedTimeRange,
        securityLevel: 'CONFIDENTIAL'
      }
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', `trustchain-export-${new Date().toISOString().split('T')[0]}.json`);
    linkElement.click();
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2 flex items-center">
            Verification Map Dashboard{isUsingDemoData && <span className="text-lg font-normal text-amber-400 ml-2" title={sampleDataDisclaimer("dashboard")}>({SAMPLE_DATA_BADGE})</span>}
          </h2>
          <p className="text-[#A8A29E]">Real-time governance and verification visualization</p>
          {globalDemoMode && (
            <p className="text-xs text-gray-500 italic mt-1">Cryptographic verification of every governance decision. Each node represents a verified agent action.</p>
          )}
        </div>
        <div className="flex items-center space-x-3">
          <Badge 
            variant="outline" 
            className={isLiveMode ? "text-green-400 border-green-400" : "text-[#A8A29E] border-gray-400"}
          >
            {isLiveMode ? '🔴 Live Feed' : '⏸️ Paused'}
          </Badge>
          <Button 
            variant="outline" 
            onClick={exportTrustChainData}
            className="border-cyan-600 text-cyan-400 hover:bg-cyan-900"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Data
          </Button>
          <select
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e.target.value)}
            className="bg-gray-900 border border-[#4A443D] rounded px-3 py-2 text-white text-sm"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
        </div>
      </div>

      {/* Key Metrics Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Total Records</p>
                <p className="text-2xl font-bold text-cyan-400">{chainMetrics.totalRecords.toLocaleString()}</p>
              </div>
              <Database className="w-8 h-8 text-cyan-400 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Override Rate</p>
                <p className="text-2xl font-bold text-ag-warning">{chainMetrics.overrideRate.toFixed(1)}%</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-ag-warning opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Avg Approval Time</p>
                <p className="text-2xl font-bold text-green-400">{chainMetrics.averageApprovalTime}s</p>
              </div>
              <Clock className="w-8 h-8 text-green-400 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Security Score</p>
                <p className="text-2xl font-bold text-purple-400">{chainMetrics.securityScore.toFixed(1)}</p>
              </div>
              <Shield className="w-8 h-8 text-purple-400 opacity-60" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Real-time Override Timeline */}
      <Card className="bg-gray-900 border-[#3A352F]">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <Activity className="w-5 h-5 mr-2 text-cyan-400" />
              Override Activity Timeline (Last 24h)
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsLiveMode(!isLiveMode)}
              className={isLiveMode ? "border-green-600 text-green-400" : "border-[#4A443D] text-[#A8A29E]"}
            >
              {isLiveMode ? 'Pause Feed' : 'Resume Feed'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={overrideTimelineData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="time" stroke="#666" />
              <YAxis stroke="#666" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1a1a1a', 
                  border: '1px solid #333',
                  borderRadius: '8px'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="approvals" 
                stackId="1"
                stroke="#44ff44" 
                fill="#44ff44" 
                fillOpacity={0.3}
                name="Approvals"
              />
              <Area 
                type="monotone" 
                dataKey="overrides" 
                stackId="1"
                stroke="#ffcc00" 
                fill="#ffcc00" 
                fillOpacity={0.6}
                name="Overrides"
              />
              <Area 
                type="monotone" 
                dataKey="alerts" 
                stackId="1"
                stroke="#ff4444" 
                fill="#ff4444" 
                fillOpacity={0.8}
                name="Security Alerts"
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Persona Authority Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-gray-900 border-[#3A352F]">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="w-5 h-5 mr-2 text-purple-400" />
              Agent Authority Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={personaDistributionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="count"
                  label={({ persona, percentage }) => `${persona.split(' ')[0]}: ${percentage}%`}
                >
                  {personaDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1a1a1a', 
                    border: '1px solid #333',
                    borderRadius: '8px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardHeader>
            <CardTitle className="flex items-center">
              <GitBranch className="w-5 h-5 mr-2 text-ag-active" />
              Chain Integrity Metrics
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#292524] p-3 rounded">
                <div className="text-sm text-[#A8A29E]">Hash Verification</div>
                <div className="text-lg font-bold text-ag-active">100%</div>
                <div className="text-xs text-gray-500">All chains verified</div>
              </div>
              <div className="bg-[#292524] p-3 rounded">
                <div className="text-sm text-[#A8A29E]">Consensus Rate</div>
                <div className="text-lg font-bold text-cyan-400">98.7%</div>
                <div className="text-xs text-gray-500">Multi-signature approval</div>
              </div>
              <div className="bg-[#292524] p-3 rounded">
                <div className="text-sm text-[#A8A29E]">Audit Coverage</div>
                <div className="text-lg font-bold text-purple-400">100%</div>
                <div className="text-xs text-gray-500">Complete trail logging</div>
              </div>
              <div className="bg-[#292524] p-3 rounded">
                <div className="text-sm text-[#A8A29E]">Compliance Score</div>
                <div className="text-lg font-bold text-yellow-400">A+</div>
                <div className="text-xs text-gray-500">SOC 2 Readiness Mapped</div>
              </div>
            </div>

            <div className="mt-4 p-3 bg-blue-900/20 border border-blue-700 rounded">
              <div className="text-sm font-semibold text-blue-300 mb-2">Verification Anchors</div>
              <div className="space-y-1 text-xs text-blue-200">
                <div>• Cryptographic hash verification</div>
                <div>• Multi-agent consensus requirements</div>
                <div>• Immutable audit trail storage</div>
                <div>• Real-time integrity monitoring</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Audit Trail */}
      <Card className="bg-gray-900 border-[#3A352F]">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <FileText className="w-5 h-5 mr-2 text-orange-400" />
              Detailed Audit Trail
            </span>
            <div className="flex space-x-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#A8A29E]" />
                <Input
                  placeholder="Search records..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64 bg-[#292524] border-[#4A443D] text-white"
                />
              </div>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredRecords.map((record, index) => (
              <div 
                key={index}
                className="p-4 bg-[#292524] rounded-lg border border-[#3A352F] hover:border-[#4A443D] transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="flex items-center space-x-2">
                        <Hash className="w-4 h-4 text-cyan-400" />
                        <span className="font-semibold text-white">{record.capsule}</span>
                      </div>
                      
                      <Badge className={getSeverityColor(record.severity)}>
                        {record.severity.toUpperCase()}
                      </Badge>
                      
                      {record.override && (
                        <Badge className="bg-orange-900 text-orange-300 border-orange-600">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          OVERRIDE
                        </Badge>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-[#A8A29E]">Approver:</span>
                        <div className="font-medium" style={{ color: getPersonaColor(record.approver) }}>
                          {getPersonaLevel(record.approver)}
                        </div>
                      </div>
                      <div>
                        <span className="text-[#A8A29E]">Action:</span>
                        <div className="text-white">{record.action.replace(/_/g, ' ')}</div>
                      </div>
                      <div>
                        <span className="text-[#A8A29E]">Duration:</span>
                        <div className="text-white">{formatDuration(record.duration)}</div>
                      </div>
                      <div>
                        <span className="text-[#A8A29E]">Timestamp:</span>
                        <div className="text-white">{new Date(record.timestamp).toLocaleString()}</div>
                      </div>
                    </div>
                    
                    <div className="mt-3 p-2 bg-gray-900 rounded font-mono text-xs">
                      <span className="text-[#A8A29E]">Hash: </span>
                      <span className="text-cyan-400">{record.hash}</span>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-[#4A443D] text-[#A8A29E] hover:bg-[#3A352F]"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-[#4A443D] text-[#A8A29E] hover:bg-[#3A352F]"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {filteredRecords.length === 0 && (
            <div className="text-center py-12">
              <Shield className="w-12 h-12 text-[#8F857B] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#A8A29E] mb-2">No records found</h3>
              <p className="text-gray-500">Try adjusting your search criteria</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Trust Score & Recommendations */}
      <Card className="bg-gradient-to-r from-blue-900 to-purple-900 border-blue-500">
        <CardHeader>
          <CardTitle className="text-white">Verification Health Assessment</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-cyan-400 mb-2">{chainMetrics.securityScore.toFixed(1)}/100</div>
              <div className="text-white font-semibold">Overall Verification Score</div>
              <div className="text-[#A8A29E] text-sm">Enterprise Grade</div>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-semibold text-white">✅ Strengths</h4>
              <ul className="text-sm text-gray-200 space-y-1">
                <li>• 100% hash verification integrity</li>
                <li>• Low override rate (12.5%)</li>
                <li>• Fast approval times (avg 34s)</li>
                <li>• Complete audit trail coverage</li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-semibold text-white">🎯 Recommendations</h4>
              <ul className="text-sm text-gray-200 space-y-1">
                <li>• Consider multi-signature for Analyst escalations</li>
                <li>• Implement time-based approval windows</li>
                <li>• Add geographic approval distribution</li>
                <li>• Schedule quarterly security reviews</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}